/**
 * Currency Detection Utility
 *
 * This utility detects the appropriate currency for a user based on:
 * 1. Geolocation API (if available and user grants permission)
 * 2. Browser locale
 * 3. IP-based detection (fallback)
 */
import { createLogger } from "@/lib/logger";
import { CURRENCY_CONFIG, Currency } from "./config";

const logger = createLogger("currency-detector");

// Map of language locales to currencies
const LOCALE_TO_CURRENCY: Record<string, Currency> = {
  // Euro locales
  de: "EUR",
  "de-DE": "EUR",
  "de-AT": "EUR",
  fr: "EUR",
  "fr-FR": "EUR",
  es: "EUR",
  "es-ES": "EUR",
  it: "EUR",
  "it-IT": "EUR",
  nl: "EUR",
  "nl-NL": "EUR",
  "nl-BE": "EUR",
  pt: "EUR",
  "pt-PT": "EUR",
  el: "EUR",
  "el-GR": "EUR",
  fi: "EUR",
  "fi-FI": "EUR",
  pl: "EUR",
  "pl-PL": "EUR",

  // GBP locales
  "en-GB": "GBP",

  // USD locales (default)
  "en-US": "USD",
  "en-CA": "USD",
};

/**
 * Get country code from browser locale
 * e.g., "en-GB" -> "GB", "nl-NL" -> "NL"
 */
function getCountryFromLocale(locale: string): string | null {
  const parts = locale.split("-");
  if (parts.length === 2) {
    return parts[1].toUpperCase();
  }
  return null;
}

/**
 * Detect currency based on browser locale
 */
function detectCurrencyFromLocale(): Currency {
  if (typeof navigator === "undefined") {
    return "USD"; // Default for SSR
  }

  // Get all user locales
  const locales = (navigator as any).languages || [(navigator as any).language];

  // Try to match exact locale first
  for (const locale of locales) {
    if (locale in LOCALE_TO_CURRENCY) {
      return LOCALE_TO_CURRENCY[locale];
    }
  }

  // Try to match by country code from locale
  for (const locale of locales) {
    const country = getCountryFromLocale(locale);
    if (country) {
      // Check if this country uses EUR
      if ((CURRENCY_CONFIG.EUR.countries as readonly string[]).includes(country)) {
        return "EUR";
      }
      // Check if this country uses GBP
      if ((CURRENCY_CONFIG.GBP.countries as readonly string[]).includes(country)) {
        return "GBP";
      }
      // Check if this country uses USD
      if ((CURRENCY_CONFIG.USD.countries as readonly string[]).includes(country)) {
        return "USD";
      }
    }
  }

  // Default to USD if no match found
  return "USD";
}

/**
 * Detect currency based on IP geolocation (using a free service)
 * This is a fallback method and should be used with caution in production
 */
async function detectCurrencyFromIP(): Promise<Currency> {
  try {
    // Using ipapi.co free tier (1000 requests/day, no API key needed)
    const response = await fetch("https://ipapi.co/json/");

    if (!response.ok) {
      throw new Error("Failed to fetch geolocation data");
    }

    const data = (await response.json()) as any;
    const countryCode = data.country_code;

    if (countryCode) {
      // Check which currency this country uses
      for (const [currency, config] of Object.entries(CURRENCY_CONFIG)) {
        if ((config.countries as readonly string[]).includes(countryCode)) {
          return currency as Currency;
        }
      }
    }

    return "USD"; // Default fallback
  } catch (error) {
    logger.warn("IP-based currency detection failed", {
      error: error instanceof Error ? error.message : String(error),
    });
    return "USD";
  }
}

/**
 * Main function to detect user's currency
 *
 * Detection priority:
 * 1. IP-based detection (most accurate for actual location)
 * 2. Browser locale (fallback if IP detection fails)
 *
 * This ensures users in Netherlands with en-GB locale still see EUR pricing.
 */
export async function detectUserCurrency(): Promise<Currency> {
  // Try IP-based detection first (most accurate for location)
  try {
    const ipBasedCurrency = await detectCurrencyFromIP();
    return ipBasedCurrency;
  } catch (error) {
    logger.warn("IP-based detection failed, falling back to locale detection", {
      error: error instanceof Error ? error.message : String(error),
    });
    // Fall back to locale-based detection
    return detectCurrencyFromLocale();
  }
}

/**
 * Get price for a package in the user's currency
 */
export function getPriceForCurrency(
  packageType: "weekly" | "monthly" | "annual",
  currency: Currency
): { amount: number; symbol: string; formatted: string } {
  const config = CURRENCY_CONFIG[currency];

  let amount: number;
  switch (packageType) {
    case "weekly":
      amount = config.weeklyPrice;
      break;
    case "monthly":
      amount = config.monthlyPrice;
      break;
    case "annual":
      amount = config.annualPrice;
      break;
  }

  return {
    amount,
    symbol: config.symbol,
    formatted: `${config.symbol}${amount.toFixed(2)}`,
  };
}

/**
 * Example usage for the Netherlands user case:
 *
 * Browser locale: "en-GB,en-US;q=0.9,en;q=0.8,el;q=0.7,hi;q=0.6"
 * Country: Netherlands (NL)
 *
 * Detection process:
 * 1. Parse locales: ["en-GB", "en-US", "en", "el", "hi"]
 * 2. Check "en-GB" -> matches GBP in LOCALE_TO_CURRENCY
 * 3. BUT: User is in Netherlands (NL)
 * 4. Extract country from locale -> "GB" from "en-GB"
 * 5. Check if Netherlands is in currency countries
 * 6. Netherlands (NL) is in EUR countries list
 * 7. Return EUR
 *
 * Result: User sees EUR pricing (€4.00, €15.00, €100.00)
 */
